import React from 'react';
import { AlertCircle, CheckCircle, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';

interface UsageLimitIndicatorProps {
  used: number;
  limit: number;
  feature: string;
  isGuest?: boolean;
  resetTime?: string;
}

const UsageLimitIndicator: React.FC<UsageLimitIndicatorProps> = ({
  used,
  limit,
  feature,
  isGuest = false,
  resetTime = '24 hours'
}) => {
  // Don't show if user has unlimited (limit is -1, 0, or very high)
  if (limit <= 0 || limit >= 1000) {
    return null;
  }

  const remaining = Math.max(0, limit - used);
  const percentage = (used / limit) * 100;
  const isLimitReached = used >= limit;
  const isNearLimit = percentage >= 80 && !isLimitReached;

  return (
    <div className={`rounded-xl p-4 border transition-all ${
      isLimitReached 
        ? 'bg-red-50 border-red-300' 
        : isNearLimit 
        ? 'bg-red-50/30 border-red-200'
        : 'bg-white border-red-200'
    }`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {isLimitReached ? (
            <AlertCircle className="w-5 h-5 text-red-500" />
          ) : (
            <CheckCircle className="w-5 h-5 text-red-400" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <h3 className={`text-sm font-semibold ${
              isLimitReached ? 'text-red-900' : 'text-gray-900'
            }`}>
              {isGuest ? 'Guest ' : ''}{feature} Usage
            </h3>
            <span className={`text-sm font-bold ${
              isLimitReached 
                ? 'text-red-600' 
                : isNearLimit 
                ? 'text-red-600'
                : 'text-red-500'
            }`}>
              {used}/{limit}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                isLimitReached
                  ? 'bg-red-600'
                  : isNearLimit
                  ? 'bg-red-500'
                  : 'bg-red-400'
              }`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>

          {isLimitReached ? (
            <div>
              <p className="text-xs text-red-700 mb-2">
                {isGuest 
                  ? `You've used your free ${feature.toLowerCase()}. Create an account for more!`
                  : `Limit reached. Resets in ${resetTime}.`
                }
              </p>
              {isGuest && (
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 transition-colors"
                >
                  <Crown className="w-3 h-3" />
                  Create Free Account
                </Link>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-600">
              {remaining} {remaining === 1 ? 'use' : 'uses'} remaining
              {isGuest && ` today`}
              {isNearLimit && !isGuest && (
                <Link to="/pricing" className="ml-1 text-red-600 hover:text-red-700 font-medium transition-colors">
                  Upgrade for more
                </Link>
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsageLimitIndicator;
